import { VERSES } from './verses.js';

/**
 * Learning paths are derived from the verse deck rather than hand-maintained,
 * so a book can never appear as an empty path and adding a verse to a new book
 * creates its path automatically.
 */

/** Canonical ordering, used to sort paths the way a Bible's contents page does. */
const CANON = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges',
  'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles',
  '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations',
  'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah',
  'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians',
  '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians', 'Colossians',
  '1 Thessalonians', '2 Thessalonians', '1 Timothy', '2 Timothy', 'Titus',
  'Philemon', 'Hebrews', 'James', '1 Peter', '2 Peter', '1 John', '2 John',
  '3 John', 'Jude', 'Revelation',
];

/** A one-line orientation for each book that currently has verses. */
const BLURBS = {
  Genesis: 'Beginnings — creation, covenant and a family God refuses to give up on.',
  Exodus: 'Rescue from slavery, and a God who gives his name and his law.',
  Leviticus: 'How a holy God makes a way to live among his people.',
  Numbers: 'Forty years in the wilderness, and a blessing still spoken today.',
  Deuteronomy: 'Moses’ last sermons: remember, love, and choose life.',
  Joshua: 'Courage at the edge of the promise.',
  Ruth: 'Loyalty in a famine, and an unlikely ancestor of a king.',
  '1 Samuel': 'Israel asks for a king; God looks at the heart.',
  '1 Kings': 'Wisdom, a temple, and a kingdom that splits in two.',
  '2 Chronicles': 'Kings, temple and the long call to return.',
  Job: 'Suffering without easy answers — and a Redeemer who lives.',
  Psalms: 'The prayer book of the Bible: praise, lament and everything between.',
  Proverbs: 'Practical wisdom for ordinary days.',
  Ecclesiastes: 'A hard-eyed look at meaning under the sun.',
  Isaiah: 'Judgement, comfort, and the servant who is pierced for us.',
  Jeremiah: 'Hope written to people in exile.',
  Lamentations: 'Grief that still finds mercies new every morning.',
  Ezekiel: 'Visions of a new heart and a restored people.',
  Daniel: 'Faithfulness under pressure in a foreign empire.',
  Micah: 'What does the LORD require of you?',
  Zephaniah: 'A God who rejoices over his people with singing.',
  Malachi: 'The Old Testament’s closing challenge.',
  Matthew: 'Jesus the promised King, and the Sermon on the Mount.',
  Mark: 'The fast-moving Gospel of the Servant.',
  Luke: 'The Gospel for outsiders, told by a careful historian.',
  John: 'Signs, “I am” sayings, and life in his name.',
  Acts: 'The Spirit sends the church to the ends of the earth.',
  Romans: 'Paul’s fullest account of the gospel.',
  '1 Corinthians': 'A messy church, and the more excellent way of love.',
  '2 Corinthians': 'Strength made perfect in weakness.',
  Galatians: 'Freedom in Christ, and the fruit of the Spirit.',
  Ephesians: 'Grace, unity and the armour of God.',
  Philippians: 'Joy and contentment, written from prison.',
  Colossians: 'Christ above all, in work and in worship.',
  '1 Thessalonians': 'Rejoice, pray, give thanks.',
  '2 Timothy': 'Paul’s last letter: guard the good deposit.',
  Hebrews: 'Jesus, better than everything that came before.',
  James: 'Faith that shows up in what you do.',
  '1 Peter': 'Hope for a church under pressure.',
  '1 John': 'Assurance, confession and love.',
  Revelation: 'The end of the story: every tear wiped away.',
};

function canonIndex(book) {
  const i = CANON.indexOf(book);
  // Unknown books sort to the end rather than to the front.
  return i === -1 ? CANON.length : i;
}

/**
 * @returns {{book:string, testament:'old'|'new', count:number, blurb:string,
 *            verseIds:string[]}[]} paths in canonical order
 */
export function buildPaths() {
  const byBook = new Map();

  for (const verse of VERSES) {
    let path = byBook.get(verse.book);
    if (!path) {
      path = {
        book: verse.book,
        testament: verse.testament,
        blurb: BLURBS[verse.book] ?? `Verses from ${verse.book}.`,
        verseIds: [],
      };
      byBook.set(verse.book, path);
    }
    path.verseIds.push(verse.id);
  }

  return [...byBook.values()]
    .map((p) => ({ ...p, count: p.verseIds.length }))
    .sort((a, b) => canonIndex(a.book) - canonIndex(b.book));
}

export const PATHS = buildPaths();
